import type { FormMetadata, Intent } from "@conform-to/react";
import type { TFunction } from "i18next";
import { boolean, z } from "zod";
export type CardSchemeName = "Visa" | "MasterCard" | "Amex" | "DinersClub" | "Discover" | "JCB"
const _16digitsMask = [/\d/, /\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/, /\d/];
const _15digitsMask = [/\d/, /\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/, /\d/, /\d/];
const _14digitsMask = [/\d/, /\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/, /\d/];

const schemes: { [name in CardSchemeName]: { pattern: RegExp, lengths: number[] } } = {
	"Visa": { pattern: /^4[0-9]{12}(?:[0-9]{3})?$/, lengths: [16] },
	"MasterCard": { pattern: /^(?:5[1-5][0-9]{14}|2[2-7][0-9]{14})$/, lengths: [16] },
	"Amex": { pattern: /^3[47][0-9]{13}$/, lengths: [15] },
	"DinersClub": { pattern: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/, lengths: [14] },
	"Discover": { pattern: /^6(?:011|5[0-9]{2})[0-9]{12}$/, lengths: [16] },
	"JCB": { pattern: /^(?:2131|1800|35[0-9]{3})[0-9]{11}$/, lengths: [16] },
}
const schemesRegexMask: { [name in CardSchemeName]: { pattern: RegExp, mask: Array<string | RegExp> } } = {
	"Visa": { pattern: /^4[0-9]*$/, mask: _16digitsMask },
	"MasterCard": { pattern: /^(?:5[1-5][0-9]*|2[2-7][0-9]*)$/, mask: _16digitsMask },
	"Amex": { pattern: /^3[47][0-9]*$/, mask: _15digitsMask },
	"DinersClub": { pattern: /^3(?:0[0-5]|[68][0-9])[0-9]*$/, mask: _14digitsMask },
	"Discover": { pattern: /^6(?:011|5[0-9]{2})[0-9]*$/, mask: _16digitsMask },
	"JCB": { pattern: /^(?:2131|1800|35[0-9]{3})[0-9]*$/, mask: _16digitsMask },
}
export function getCvvMask(cardNumber?: string) {
	cardNumber = cardNumber?.replace(/\D/g, "") ?? "";
	const scheme = identifyCardScheme(cardNumber);
	if (scheme === "Amex") return [/\d/, /\d/, /\d/, /\d/];
	return [/\d/, /\d/, /\d/];
}
export function getCreditCardMask(cardNumber: string) {
	cardNumber = cardNumber.replace(/\D/g, "");
	for (const name of Object.keys(schemesRegexMask) as Array<keyof typeof schemesRegexMask>) {
		const scheme = schemesRegexMask[name];
		if (scheme.pattern.test(cardNumber)) {
			return scheme.mask;
		}
	}
	return _16digitsMask;
}
function validateCreditCard(cardNumber: string | undefined, tError: TFunction<"error", undefined>) {
	if (!cardNumber) return tError("payment.invalid_number");
	cardNumber = cardNumber.replace(/\D/g, "");


	// Check if card number is valid using Luhn algorithm
	const isValid = luhnCheck(cardNumber);
	if (!isValid) return tError("payment.invalid_number");

	// Identify the card scheme
	const scheme = identifyCardScheme(cardNumber);
	if (scheme === "Unknown") return tError("payment.invalid_number");
	const { pattern, lengths } = schemes[scheme];
	if (lengths.includes(cardNumber.length)) {
		// return pattern.test(cardNumber) ? undefined : "Please enter a valid card number";
		return undefined;
	} else {
		return tError("payment.invalid_number");
	}
}
function luhnCheck(cardNumber: string) {
	cardNumber = cardNumber.replace(/\D/g, "");
	let sum = 0;
	let shouldDouble = false;

	// Process digits from right to left
	for (let i = cardNumber.length - 1; i >= 0; i--) {
		let digit = parseInt(cardNumber[i], 10);

		if (shouldDouble) {
			digit *= 2;
			if (digit > 9) digit -= 9; // Subtract 9 if the result is a double-digit
		}

		sum += digit;
		shouldDouble = !shouldDouble;
	}

	return sum % 10 === 0; // Valid if sum modulo 10 is 0
}
export function identifyCardScheme(cardNumber: string) {
	cardNumber = cardNumber.replace(/\D/g, "");
	for (const name of Object.keys(schemesRegexMask) as Array<keyof typeof schemesRegexMask>) {
		const scheme = schemesRegexMask[name];
		if (scheme.pattern.test(cardNumber)) {
			return name;
		}
	}
	return "Unknown";
}
function validateCVV(cardNumber: string, cvv: string | undefined, tError: TFunction<"error", undefined>) {
	if (!cvv) return tError("cvv_required");
	const sanitizedCVV = cvv.replace(/\D/g, "");
	const cardScheme = identifyCardScheme(cardNumber);
	const expectedLength = cardScheme === "Amex" ? 4 : 3;
	return sanitizedCVV.length === expectedLength ? undefined : tError("invalid_cvv_length", { expectedLength });
}
function validateExpiryDate(expiryDate: string | undefined, tError: TFunction<"error", undefined>) {
	if (!expiryDate) return tError("payment.invalid_expiry_month_year");
	// Check if the input format is MM/YY using a regular expression
	const regex = /^(0[1-9]|1[0-2])\/\d{2}$/;
	if (!regex.test(expiryDate)) {
		return tError("payment.invalid_expiry_month_year");
	}
	const [month, year] = expiryDate.split("/").map(Number);

	// Get the current month and year
	const currentDate = new Date();
	const currentMonth = currentDate.getMonth() + 1; // Months are 0-based, so add 1
	const currentYear = currentDate.getFullYear() % 100; // Get last two digits of the year

	// Validate if the expiry year is in the future or is the current year with a future month
	if (year < currentYear || (year === currentYear && month < currentMonth)) {
		return tError("payment.invalid_expiry_month_year");
	}
	return undefined
}
function validateFullName(fullName: string | undefined, tError: TFunction<"error", undefined>) {
	if (!fullName) return tError("full_name_required");
	// Trim whitespace from the beginning and end of the string
	const trimmedName = fullName.trim();

	// Regular expression to check for a valid full name
	const regex = /^[A-Za-z]+([ '-][A-Za-z]+)*$/;

	// Check if the name is not too short or too long
	if (trimmedName.length < 2 || trimmedName.length > 100) {
		return tError("invalid_full_name");
	}

	// Split the name into parts by space
	const nameParts = trimmedName.split(" ");

	// Ensure there are at least two parts (first name and last name)
	if (nameParts.length < 2) {
		return false; // Must contain at least a first and last name
	}

	// Validate against the regular expression
	if (!regex.test(trimmedName)) {
		return tError("invalid_full_name");
	}
	return undefined;
}
export const paymentMethodSchema = z.enum(["credit-card", "stored-card", "paypal", "voucher", "zero"])
export type PaymentMethodType = z.infer<typeof paymentMethodSchema>;

const baseSchema = z.object({
	walletAmountUsed: z.number(),
	paymentMethod: paymentMethodSchema,
	cardNumber: z.string().optional(),
	creditCardToken: z.string().optional(),
	expirationDate: z.string().optional(),
	cvv: z.string().optional(),
	fullName: z.string().optional(),
	address: z.object({
		address1: z.string().optional(),
		city: z.string().optional(),
		state: z.string().optional(),
		country: z.string().optional(),
		zipCode: z.string().optional(),
	}).optional(),
	voucherCode: z.string().optional(),
	selected: z.number().optional(),
	tokens: z.array(z.object({
		permanentTokenId: z.number().optional(),
		cvv: z.string().optional(),
		schemeName: z.string().optional(),
	})).optional(),
	store: z.coerce.boolean(),
	guestEmail: z.string().optional(),
});
// export const paymentSchema = baseSchema.extend({ cartId: z.string() }).superRefine(superRefine);
// export const autoPaymentSchema = baseSchema.extend({ profileId: z.number() }).superRefine(superRefine);

function superRefine(data: z.infer<typeof baseSchema>, context: z.RefinementCtx, tError: TFunction<"error", undefined>) {
	if (data.paymentMethod === "credit-card") {
		const cardNumberResult = validateCreditCard(data.cardNumber, tError);
		if (cardNumberResult) context.addIssue({ code: "custom", path: ["cardNumber"], message: cardNumberResult })
		const cvvResult = data.cardNumber ? validateCVV(data.cardNumber, data.cvv, tError) : undefined;
		if (cvvResult) context.addIssue({ code: "custom", path: ["cvv"], message: cvvResult });
		const mmYYresult = validateExpiryDate(data.expirationDate, tError);
		if (mmYYresult) context.addIssue({ code: "custom", path: ["expirationDate"], message: mmYYresult });
		const nameResult = validateFullName(data.fullName, tError);
		if (nameResult) context.addIssue({ code: "custom", path: ["fullName"], message: nameResult });
		if (!data.address?.country) context.addIssue({ code: "custom", path: ["address.country"], message: tError("invalid_country") });
		if (["GB", "CA", "US"].includes(data.address?.country ?? "")) {
			if (!data.address?.address1) context.addIssue({ code: "custom", path: ["address.address1"], message: tError("invalid_street") });
			if (!data.address?.state) context.addIssue({ code: "custom", path: ["address.state"], message: tError("invalid_state") });
			if (!data.address?.city) context.addIssue({ code: "custom", path: ["address.city"], message: tError("invalid_city") });
			if (!data.address?.zipCode) context.addIssue({ code: "custom", path: ["address.zipCode"], message: tError("invalid_zip") });
		}
		return;
	}
	if (data.paymentMethod === "stored-card") {
		if (!data.selected) {
			context.addIssue({ code: "custom", path: ["selected"], message: tError("select_stored_card"), })
		}
		const selectedTokenIndex = data.tokens?.findIndex(x => x.permanentTokenId === data.selected) ?? -1;
		const selectedToken = selectedTokenIndex > -1 ? data.tokens?.[selectedTokenIndex] : undefined;
		if (data.selected && !selectedToken) {
			context.addIssue({ code: "custom", path: ["selected"], message: tError("select_stored_card"), })
		}
		if (selectedToken) {
			if (!selectedToken.cvv) {
				context.addIssue({ code: "custom", path: [`tokens[${selectedTokenIndex}].cvv`], message: tError("payment.invalid_cvc"), })
			} else {
				const cvvLength = selectedToken.schemeName?.toLowerCase() === "amex" ? 4 : 3;
				if (selectedToken.cvv.length !== cvvLength) {
					context.addIssue({ code: "custom", path: [`tokens[${selectedTokenIndex}].cvv`], message: tError("payment.invalid_cvc"), })
				}
			}
		}
		return;
	}
	if (data.paymentMethod === "voucher") {
		if (!data.voucherCode) context.addIssue({ code: "custom", path: ["voucherCode"], message: tError("invalid_voucher_code") });
		return;
	}
}

// export type AutoPaymentSchema = z.infer<typeof autoPaymentSchema>;
// export type PaymentSchema = z.infer<typeof paymentSchema>;
export type PaymentSchemaFieldset = ReturnType<FormMetadata<z.infer<typeof baseSchema>, string[]>["getFieldset"]>;

export function createPaymentSchema(tError: TFunction<"error", undefined>) {
	return baseSchema
		.extend({ cartId: z.string() })
		.superRefine((data, context) => superRefine(data, context, tError));
}

export function createAutoPaymentSchema(tError: TFunction<"error", undefined>) {
	return baseSchema
		.extend({ profileId: z.number() })
		.superRefine((data, context) => superRefine(data, context, tError));
}
export function getPaymentError(tError: TFunction<"error", undefined>, responseCode?: string) {
	if (!responseCode) return "Payment failed";
	const key = `payment.${responseCode}`;
	const error = tError(key as any);
	if (error === key) return "Payment failed";
	return error;
}
export type PaymentSchema = z.infer<ReturnType<typeof createPaymentSchema>>;
export type AutoPaymentSchema = z.infer<ReturnType<typeof createAutoPaymentSchema>>